/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {preventDefault, ResponseData, showDialog, validateEmail} from '@dvrd/dvr-controls';
import {CredentialResponse} from '@react-oauth/google';
import {AppContext} from '../../misc/appContext';
import {googleLogin, login} from '../../../util/requestSender';
import User from '../../../models/userModel';
import {ActivateHandle, LoginErrors} from '../../../util/interfaces';
import {setSentryUser} from '../../../util/sentryUtil';
import {sendGaEvent, sendGaPageView, setGaUserId} from '../../../util/analytics';
import Login from './login';
import {setNavigationParam} from '../../../util/navigateUtil';
import ValidateTwoFactorController from '../../twoFactor/validate/validateTwoFactorController';
import {showValidationErrors} from '../../../util/componentUtil';
import {useNavigate} from 'react-router';
import {isValidPath} from '../../../routes';
import {FORGOT_PASSWORD_ROUTE, LOGIN_ROUTE, ORDERS_ROUTE, REGISTER_ROUTE} from '../../../util/constants';
import IpVerificationController from "../ipVerification/ipVerificationController";
import {getFingerprint} from "../../../util/utils";

const emptyErrors: LoginErrors = {email: null, password: null, general: null};

function determineRedirect(): string | null {
    const redirect = new URLSearchParams(location.search).get('redirect');
    if (!redirect) return null;
    if (isValidPath(redirect)) return redirect;
    return null;
}

export default function LoginController() {
    const context = useContext(AppContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<LoginErrors>(emptyErrors);
    const [loading, setLoading] = useState(false);
    const mfaRef = useRef<ActivateHandle>(null);
    const ipVerificationRef = useRef<ActivateHandle>(null);
    const navigate = useNavigate();
    const redirectURL = useRef<string | null>(determineRedirect());

    function onGoogleLogin(response: CredentialResponse) {
        const token = response.credential;
        if (!token) return onGoogleError();
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            googleLogin({
                data: {token, fp: fingerprint}, callback: (data: ResponseData) => {
                    requestCallback(data);
                }
            });
        })
    }

    function onGoogleError() {
        showDialog('Er is iets missgegaan bij het verbinden met Google.', 'Inloggen mislukt');
    }

    function onChange(key: 'email' | 'password') {
        return function(value: string) {
            if (key === 'email') setEmail(value);
            else setPassword(value);
            setErrors({...errors, [key]: null});
        };
    }

    function onLogin() {
        onSubmit();
    }

    function onSubmit(params?: {email?: string; password?: string}) {
        sendGaEvent('Click', 'Inloggen', 'Log in');
        if (!validateValues(params)) return;
        setLoading(true);
        const _email = params?.email ?? email;
        const _password = params?.password ?? password;
        getFingerprint().then((fingerprint: string) => {
            login({
                data: {email: _email, password: _password, fp: fingerprint}, callback: (data: ResponseData) => {
                    requestCallback(data);
                }
            });
        });
    }

    function onFocusField(key: keyof LoginErrors) {
        return function() {
            setErrors({...errors, [key]: null});
        };
    }

    function onClickRegister() {
        sendGaEvent('Click', 'Inloggen', 'Registreer nu');
        navigate(REGISTER_ROUTE);
    }

    function onClickGoogle() {
        sendGaEvent('Click', 'Inloggen', 'Google login');
    }

    function onClickReset(evt: React.MouseEvent) {
        preventDefault(evt);
        setNavigationParam(email);
        navigate(FORGOT_PASSWORD_ROUTE);
    }

    function onTwoFactorSuccess(user: Record<string, any>) {
        requestCallback({user, success: true, status: 'success'});
    }

    function validateValues(params?: {email?: string; password?: string}): boolean {
        let valid = true;
        const _errors = {...errors};
        const _email = params?.email ?? email;
        const _password = params?.password ?? password;
        if (!_email.length) {
            _errors.email = 'Vul je e-mailadres in';
            valid = false;
        } else if (!validateEmail(_email)) {
            _errors.email = 'Vul een geldig e-mailadres in';
            valid = false;
        } else if (!_password.length) {
            _errors.password = 'Vul je wachtwoord in';
            valid = false;
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    function requestCallback(data: ResponseData) {
        setLoading(false);
        if (!data.success) {
            if (data.type === 'blocked' && data.message)
                return showDialog(data.message, 'Account geblokkeerd');
            else if (data.type === 'validation_error' && data.errors)
                return showValidationErrors(data.errors);
            else if (data.type === 'google_account')
                return showDialog('Dit e-mailadres is geregistreerd met Google. Je kunt inloggen via de Google ' +
                    'inloggen knop.', 'Google account');
            else return setErrors({...errors, general: data.message ?? 'Er is iets misgegaan'});
        }
        successCallback(data);
    }

    function successCallback(data: ResponseData) {
        if (data.two_factor_id)
            // Requires two-factor authentication
            return mfaRef.current?.activate(data.two_factor_id);
        else if (data.verification_id)
            // Requires IP verification
            return ipVerificationRef.current?.activate(data.verification_id, email);
        const user = data.user instanceof User ? data.user : new User(data.user);
        setGaUserId(user.id);
        const firstAccount = user.accounts.length ? user.accounts[0] : null;
        context.onChangeContext({
            user,
            accounts: user.accounts,
            account: firstAccount
        });
    }

    function checkDemoLogin() {
        const params = new URLSearchParams(location.search);
        if (params.get('demo') !== '1') return;
        const email = 'demo@bolmate.nl';
        const password = 'bolmate_demo';
        setEmail(email);
        setPassword(password);
        onSubmit({email, password});
    }

    useEffect(() => {
        if (!context.userContext.user) return;
        const _user = context.userContext.user;
        setGaUserId(_user.id);
        const firstAccount = _user.accounts.length ? _user.accounts[0] : null;
        setSentryUser(_user, firstAccount);
        if (firstAccount) {
            if (redirectURL.current) navigate(redirectURL.current);
            else navigate(ORDERS_ROUTE);
        } else navigate('/account-instellen');
    }, [context.userContext.user]);

    useEffect(() => {
        sendGaPageView(LOGIN_ROUTE);
        checkDemoLogin();
    }, []);

    return (
        <>
            <Login onSubmit={onLogin} onChange={onChange} email={email} password={password}
                   loading={loading} errors={errors} onFocusField={onFocusField}
                   onClickRegister={onClickRegister} onGoogleLogin={onGoogleLogin}
                   onClickGoogle={onClickGoogle} onClickReset={onClickReset} onGoogleError={onGoogleError}/>
            <ValidateTwoFactorController onSuccess={onTwoFactorSuccess} ref={mfaRef}/>
            <IpVerificationController onSuccess={onTwoFactorSuccess} ref={ipVerificationRef}/>
        </>
    );
}